import type { ReactiveController } from "lit";
import type { ShopifyProduct, ShopifyProductVariant } from "src/api/ShopifyApi";
import type {
  ProductFormOption,
  ProductFormOptions,
} from "src/utils/ProductUtil";
import type { WishlistElement } from "src/WishlistElement";

export class ProductFormController implements ReactiveController {
  host: WishlistElement;
  initializedVariantId?: number | null;
  product: ShopifyProduct | null = null;

  selectedVariant: ShopifyProductVariant | null = null;
  selectedOptions: ProductFormOptions = {};
  hasSelection: boolean = false;
  optionsWithValues: ProductFormOption[] = [];
  hasOnlyDefaultVariant: boolean = false;

  constructor(host: WishlistElement) {
    (this.host = host).addController(this);
  }

  set submitting(value: boolean) {
    this.host.querySelector("form")?.classList.toggle("wk-submitting", value);
  }

  get submitting(): boolean {
    return (
      this.host.querySelector("form")?.classList.contains("wk-submitting") ??
      false
    );
  }

  setProduct({
    product,
    selectedVariantId,
    autoSelect = false,
  }: {
    product: ShopifyProduct;
    selectedVariantId?: number;
    autoSelect?: boolean;
  }) {
    if (!product?.variants.length) {
      return;
    }

    this.product = product;
    this.initFormState({
      selectedVariantId,
      autoSelect,
    });
  }

  initFormState({
    selectedVariantId,
    autoSelect,
  }: {
    selectedVariantId?: number;
    autoSelect?: boolean;
  }) {
    if (!this.product) {
      throw new Error("Product not set");
    }

    const variant =
      this.initializedVariantId !== selectedVariantId
        ? this.product.variants.find(
            (variant) => variant.id === selectedVariantId
          )
        : this.selectedVariant;

    this.initializedVariantId = selectedVariantId;

    const options = this.host.app.utils.product.getVariantOptions({
      variant,
    });

    this.updateFormState({
      options,
      autoSelect,
    });
  }

  updateFormState({
    options,
    change,
    autoSelect = false,
  }: {
    options: ProductFormOptions;
    change?: ProductFormOptions;
    autoSelect?: boolean;
  }) {
    if (!this.product) {
      throw new Error("Product not set");
    }

    const {
      hasSelection,
      optionsWithValues,
      selectedOptions,
      selectedVariant,
      hasOnlyDefaultVariant,
    } = this.host.app.utils.product.getFormState({
      product: this.product,
      options,
      change,
      autoSelect,
    });

    this.hasSelection = hasSelection;
    this.optionsWithValues = optionsWithValues;
    this.selectedOptions = selectedOptions;
    this.selectedVariant = selectedVariant;
    this.hasOnlyDefaultVariant = hasOnlyDefaultVariant;

    this.host.requestUpdate();
  }

  changeOption({
    input,
    autoSelect = false,
  }: {
    input: HTMLInputElement;
    autoSelect?: boolean;
  }) {
    if (!this.product) {
      throw new Error("Product not set");
    }

    const change = this.getInputOption(input);

    if (!change) {
      throw new Error("Cannot parse options from input");
    }

    this.updateFormState({
      options: this.selectedOptions,
      change,
      autoSelect,
    });
  }

  addToCart({
    wishlistId,
    wishlistItemId,
    quantity,
    sectionsToRender,
    sectionsUrl = "/",
  }: {
    wishlistId: string;
    wishlistItemId: string;
    quantity?: number;
    sectionsToRender?: string[];
    sectionsUrl?: string;
  }) {
    if (!this.selectedVariant) {
      throw new Error("No variant selected");
    }

    const formElement = this.host.querySelector("form") as HTMLFormElement;

    if (!formElement) {
      throw new Error("Form not found");
    }

    this.submitting = true;

    return this.host.app
      .addToCart({
        wishlistId,
        wishlistItemId,
        variantId: this.selectedVariant.id,
        quantity:
          quantity ??
          this.host.app.utils.product.getFormQuantity({
            form: formElement,
          }),
        properties: this.host.app.utils.product.getFormProperties({
          form: formElement,
        }),
        sectionsToRender,
        sectionsUrl,
      })
      .finally(() => {
        this.submitting = false;
      });
  }

  getInputOption(element: HTMLInputElement) {
    if (!this.product) {
      throw new Error("Product not set");
    }

    return this.host.app.utils.product.getInputOption({
      input: element,
      product: this.product,
    });
  }

  hostConnected() {}
  hostDisconnected() {}

  hostUpdate() {}
  hostUpdated() {}
}
